import { Location } from "@angular/common";
import { Component, OnInit, HostListener, ElementRef, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { APIServiceService } from "src/app/api/apiservice.service";
import { CustomerSearchComponent } from "src/app/components/customer-search/customer-search.component";
import { Appointment } from "src/app/models/appointment.model";
import { Customer } from "src/app/models/customer.model";
import { DaysAgoPipe } from "src/app/pipes/days-ago.pipe";
import { AppointmentEditorService } from "src/app/services/appointment-editor.service";
import { AlgoliaCustomerHit } from "src/interfaces";

type SortOrder = 'asc' | 'desc';
type SortField = 'name' | 'email' | 'tel' | 'firstAppointmentStartTime' | 'lastAppointmentStartTime' | 'daysSinceLastAppointment' | null;

interface ISearchMatch { value: string, matchLevel: string}
interface ISearchMatches {
  firstNameMatched: ISearchMatch;
  lastNamematched: ISearchMatch;
  addressMatched: ISearchMatch;
  horsesMatched: ISearchMatch;
}
@Component({
  selector: 'ubc-customers-page',
  templateUrl: './customers-page.component.html',
  styleUrls: ['./customers-page.component.scss']
})
export class CustomersPageComponent implements OnInit {

  customers: Customer[] = [];
  currentPage: number = 0;
  pageSize: number = 50;
  isLoading: boolean = false;
  hasMoreCustomers: boolean = true;
  currentSortField: SortField = 'daysSinceLastAppointment';
  currentSortOrder: SortOrder = 'asc';

  hasSearchResults = false
  

  @ViewChild('tableContainer') tableContainer: ElementRef;

  constructor(
    private apiService: APIServiceService, 
    private router: Router,
    private appointmentEditorService: AppointmentEditorService,
    private location: Location,
  ) {

  }

  ngOnInit(){
    this.listCustomers()
  }

  async listCustomers(page: number = 0){
    if (this.isLoading || (page > 0 && !this.hasMoreCustomers)) {
      return;
    }

    try {
      this.isLoading = true;
      const response = await this.apiService.customer.list(
        page, 
        this.pageSize, 
        this.currentSortField, 
        this.currentSortField ? this.currentSortOrder : undefined
      );
      
      const newCustomers = response.data.results.map((result) => {
        return new Customer().deserialize(result);
      });
      
      if (page === 0) {
        this.customers = newCustomers;
      } else {
        this.customers = [...this.customers, ...newCustomers];
      }
      
      // Check if we've reached the end of the data
      this.hasMoreCustomers = true;
      this.currentPage = page;
    } catch(e){
      console.error('Error loading customers:', e);
    } finally {
      this.isLoading = false;
    }
  }

  sortBy(field: SortField) {
    if (this.currentSortField === field) {
      // Toggle sort order if the same field is clicked again
      this.currentSortOrder = this.currentSortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // Set new sort field and default to ascending order
      this.currentSortField = field;
      this.currentSortOrder = 'asc';
    }
    
    // Reset pagination and reload data
    this.currentPage = 0;
    this.customers = [];
    this.hasMoreCustomers = true;
    this.listCustomers();
  }

  getSortIcon(field: SortField): string {
    if (this.currentSortField !== field) {
      return 'sort';
    }
    return this.currentSortOrder === 'asc' ? 'sort-up' : 'sort-down';
  }

  @HostListener('scroll', ['$event'])
  onTableScroll(event: Event) {
    
    // Get the scroll position
    const target = event.target as HTMLElement;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;
    
    // If we're near the bottom (within 50px), load more data
    if (scrollHeight - scrollPosition <= 50 && !this.isLoading && this.hasMoreCustomers) {
      
      this.loadMoreCustomers();
    }
  }

  loadMoreCustomers() {
    this.listCustomers(this.currentPage + 1);
  }


  customerPickedFromSearch(result: any){
    console.log("Customer picked ", result)
  }

  searchResultsUpdated(customers: Customer[]){
    this.currentPage = 0
    if(customers.length === 0){
      this.hasSearchResults = false
      this.listCustomers()
      return
    }

    this.customers = customers
    this.hasSearchResults = true

    
  }



  findMatchReason(customer: Customer): string[]{
    const highlighTResult = customer.algoliaHit._highlightResult
    let addressMatched
    let firstNameMatched = (highlighTResult.firstName.matchLevel !== 'none') ? highlighTResult.firstName : undefined
    let lastNamematched = (highlighTResult.lastName.matchLevel !== 'none') ? highlighTResult.lastName : undefined
    let horsesMatched



    if(highlighTResult.addresses && highlighTResult.addresses.length > 0){
      addressMatched = highlighTResult.addresses.find((address) => { return address.line1?.matchLevel !== 'none' })
    }

    if(highlighTResult.horses && highlighTResult.horses.length > 0){
      horsesMatched = highlighTResult.horses.find((horse) => { return horse.name?.matchLevel !== 'none' })
    }


    const matches = {
      firstNameMatched,
      lastNamematched,
      addressMatched,
      horsesMatched
    }
    
    let out = []
    if(matches.firstNameMatched){
      out.push(`First name ${matches.firstNameMatched.value}`)
    }
    if(matches.lastNamematched){
      out.push(`Last name ${matches.lastNamematched.value}`)
    }
    if(matches.addressMatched){
      out.push(`Address ${matches.addressMatched.line1.value}`)
    }
    if(matches.horsesMatched){
      out.push(`Horse ${matches.horsesMatched.name.value}`)
    }

    
    return out
  }

  async openAppointment(appointmentId: number){
    if(!appointmentId){
      return
    }

    const response = await this.apiService.appointments.getByID(appointmentId)

    if(!response.data){
      return
    }

   
    this.addUrlParam("appt", `${appointmentId}`)
    this.appointmentEditorService.setOpen(response.data as Appointment)

  }

  addUrlParam(key: string, value: string ) {
    // Get the current URL path
    const currentPath = this.location.path().split('?')[0];
  
    // Get the current query parameters
    const searchParams = new URLSearchParams(this.location.path().split('?')[1]);
    // Add the parameter you want to add
    if(!searchParams.has(key)){
      searchParams.append(key, value)
    } else {
      searchParams.set(key, value)
    }
  
    // Construct the new query string
    const queryParams = searchParams.toString();
  
    // Update the URL
    this.location.replaceState(`${currentPath}?${queryParams}`);
  }

}
